import { useToast } from 'native-base';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHeapContext } from '../../../../contexts/heapContext';
import {
  useAddSurveyResourceBookmarkMutation,
  useHubSurveyBookmarkedResourcesQuery,
  useRemoveSurveyResourceBookmarkMutation,
} from '../../../../graphQL';

export type BookmarkTarget = { id: number; name?: string; recommendationAreaId?: number };

type UseSurveyBookmarksReturn = {
  loading: boolean;
  bookmarkedIds: number[];
  isBookmarked: (id: number) => boolean;
  toggle: (target: BookmarkTarget) => Promise<void>;
};

const stubAsync = async (): Promise<void> => {};
const stubReturn: UseSurveyBookmarksReturn = {
  loading: true,
  bookmarkedIds: [],
  isBookmarked: () => false,
  toggle: stubAsync,
};

export const useSurveyBookmarks = (
  surveyResultId: number | null | undefined,
): UseSurveyBookmarksReturn => {
  const toast = useToast();
  const { trackEvent } = useHeapContext();

  const { data, loading } = useHubSurveyBookmarkedResourcesQuery({
    skip: surveyResultId == null,
  });
  const serverIds = data?.hubSurveyBookmarkedResources.bookmarkedResourceIds ?? [];

  const [optimisticAdds, setOptimisticAdds] = useState<Set<number>>(new Set());
  const [optimisticRemoves, setOptimisticRemoves] = useState<Set<number>>(new Set());

  const [addMut] = useAddSurveyResourceBookmarkMutation();
  const [removeMut] = useRemoveSurveyResourceBookmarkMutation();

  const effectiveIds = useMemo(() => {
    const effectiveSet = new Set(serverIds);
    optimisticAdds.forEach(id => effectiveSet.add(id));
    optimisticRemoves.forEach(id => effectiveSet.delete(id));
    return effectiveSet;
  }, [serverIds, optimisticAdds, optimisticRemoves]);

  const isBookmarked = useCallback((id: number) => effectiveIds.has(id), [effectiveIds]);

  const add = useCallback(
    async ({ id, name, recommendationAreaId }: BookmarkTarget): Promise<void> => {
      setOptimisticAdds(prev => new Set(prev).add(id));
      // is setting an optimistic remove necessary here? isn't that for the remove?
      setOptimisticRemoves(prev => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });

      try {
        await addMut({
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          variables: { organizationResourceId: id, surveyResultId: surveyResultId! },
        });
        trackEvent('survey_resource_bookmark_save', {
          assessmentRecommendationId: recommendationAreaId,
          organizationResourceId: id,
        });
        // Do NOT clear optimistic add yet; wait until serverIds reflect it to avoid flicker
        toast.show({ description: `${name} saved to your resource library` });
      } catch {
        // Roll back optimistic add on failure
        setOptimisticAdds(prev => {
          if (!prev.has(id)) return prev;
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        toast.show({ description: 'Unable to save resource' });
      }
    },
    [addMut, surveyResultId, toast],
  );

  const remove = useCallback(
    async ({ id, name, recommendationAreaId }: BookmarkTarget): Promise<void> => {
      setOptimisticRemoves(prev => new Set(prev).add(id));
      setOptimisticAdds(prev => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });

      try {
        await removeMut({
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          variables: { organizationResourceId: id, surveyResultId: surveyResultId! },
        });
        // Keep optimistic remove until server confirms (prevents flicker)
        toast.show({ description: `${name} has been removed from your saved resources` });
        trackEvent('survey_resource_bookmark_unsave', {
          assessmentRecommendationId: recommendationAreaId,
          organizationResourceId: id,
        });
      } catch {
        // Roll back optimistic remove on failure
        setOptimisticRemoves(prev => {
          if (!prev.has(id)) return prev;
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        toast.show({ description: 'Unable to unsave resource' });
      }
    },
    [removeMut, surveyResultId, toast],
  );

  const toggle = useCallback(
    async (target: BookmarkTarget): Promise<void> => {
      if (isBookmarked(target.id)) {
        await remove(target);
      } else {
        await add(target);
      }
    },
    [isBookmarked, add, remove],
  );

  // Reconcile optimistic sets once the server result reflects the change
  useEffect(() => {
    if (serverIds.length === 0) return;
    setOptimisticAdds(prev => {
      if (prev.size === 0) return prev;
      let changed = false;
      const next = new Set(prev);
      prev.forEach(id => {
        if (serverIds.includes(id)) {
          next.delete(id);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    setOptimisticRemoves(prev => {
      if (prev.size === 0) return prev;
      let changed = false;
      const next = new Set(prev);
      prev.forEach(id => {
        if (!serverIds.includes(id)) {
          next.delete(id);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [serverIds]);

  if (surveyResultId === null || surveyResultId === undefined) {
    return stubReturn;
  }

  return {
    loading,
    bookmarkedIds: Array.from(effectiveIds),
    isBookmarked,
    toggle,
  };
};
